import { ComparatorContext } from '@kizi/comparator-core';
import { Div, DivProps, P, Span } from '@kizi/core-components';
import { IconV2 as Icon, Popover, RadioGroup } from '@kizi/shared-components';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { withFormProps } from './HOCs/withFormProps';
import { useSortTracking } from '../utils/hooks';

const FormRadioGroup = withFormProps(RadioGroup);

interface SortingOptionDependent {
  dependenceProperty: string;
  dependenceValue: string;
}

export interface SortingOption {
  icon: string;
  label: string;
  value: number;
  dependsOn?: SortingOptionDependent;
}

export interface SortingProps extends DivProps {
  onItemClick: Function;
  options: SortingOption[];
  selected: number;
  title: string;
}

export const Sorting: React.FC<SortingProps> = ({
  onItemClick,
  options,
  selected,
  title,
  ...restProps
}: SortingProps) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(selected);
  const [sortOptions, setSortOptions] = useState(options);
  const isInitialized = useRef(false);
  const { name } = useContext(ComparatorContext);
  const isOs = name.includes('os-') || name === 'moneysure-event-based';
  const { trackSort } = useSortTracking();
  let iconName = 'big-arrow-down';

  if (isOpen) {
    iconName = 'big-arrow-up';
  }

  useEffect(() => {
    setSortOptions(options);
  }, [options]);

  useEffect(() => {
    setCurrentIndex(selected);
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }
    trackSort(sortOptions[selected].label);
  }, [selected]);

  function toggleIsOpenState() {
    setIsOpen(!isOpen);
  }

  const currentItem = sortOptions.find((item) => item.value === currentIndex);

  return (
    <Div {...restProps}>
      <Div display="flex" flexDirection="column" variant="filterSorting">
        <Div
          display="flex"
          justifyContent="flex-end"
          onClick={toggleIsOpenState}
          style={{ cursor: 'pointer' }}
        >
          <Span
            style={{ cursor: 'pointer' }}
            variant={isOs ? 'iconSortingTextItem' : 'iconTextItem'}
          >
            <Icon name={isOs ? 'newSorting' : 'sorting'} mr={2} />
            {!isOs && (
              <Span color="main.2" display={['none', null, 'initial']} mr={1}>
                {title}
              </Span>
            )}
          </Span>
          {currentItem && (
            <Span
              ref={spanRef}
              variant={isOs ? 'iconSortingTextItem' : 'iconTextItem'}
            >
              {currentItem.label}
              <Icon
                fill={isOs ? 'main.1' : 'second.0'}
                width={isOs ? '16px!important' : 18}
                height={isOs ? '16px!important' : 18}
                my="auto"
                ml={isOs ? 2 : null}
                name={iconName}
              />
            </Span>
          )}
        </Div>
      </Div>
      <Popover
        arrow={true}
        attachTo={spanRef.current}
        onOutsideClick={toggleIsOpenState}
        position="bottom-right"
        show={isOpen}
        containerProps={{ zIndex: 99999 }}
      >
        <Div variant="primaryContainer">
          <P mb={2} variant="headlineImportant">
            {title}
          </P>
          <FormRadioGroup
            containerProps={{
              flexDirection: 'row-reverse',
              justifyContent: 'flex-end',
            }}
            onChange={(e) => {
              const value = Number((e.target as HTMLInputElement).value);
              onItemClick(value);
              setCurrentIndex(value);
            }}
            name="radio-group-sorting"
            radios={sortOptions.map((field: SortingOption) => {
              return {
                ...field,
                variant: 'main',
                mr: 2,
                my: 1
              };
            })}
            value={String(currentIndex)}
          />
        </Div>
      </Popover>
    </Div>
  );
};
